// Balances by wallet class.
//
// Grouped by the register's class, because the class is the thing with
// operational meaning: only Hot pays a customer; Merchant Hot is isolated and
// pays merchants only; Flagged is a case file with a balance attached, whose
// target state is zero by resolution. The words are the operator's (16 Oct
// 2026, contracts/wallet.mjs CLASS_WORDS); risk shows as a colour band.
//
// On the design system (22 Oct 2026): a card is its figure and its rows; what
// each wallet is for sits behind Details.
//
// Screens' round 2 (23 Oct 2026): the move sheet is mounted outside the
// list's loading, failed and empty branches, so a refresh that fails while a
// move is queued keeps the sheet, its result and its request id; a list read
// once stays on screen through a failed refresh; nothing registered is an
// empty state with the way in, not a warning about figures.

import { useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { Link } from 'react-router-dom';
import { custody as api, type WalletBalance, type Pool, type RenewalRow, type RenewalClass } from '../../lib/api';
import { money, relativeTime, walletName, CLASS_WORD } from '../../lib/custody-format';
import { dueWord, retiredLine, aboveZero } from '../../lib/custody-renewals';
import { poolTotal, listState } from '../../lib/custody-screen';
import { ReadFailed } from './ReadFailed';
import { Card, StateLine, Pending, Address, Pill, MoreDetails, EmptyState } from '../../components/kit';
import { Lock, Store, CheckCircle2, Flag, Zap, ArrowRightToLine } from 'lucide-react';
import { MoveSheet, type MoveDestinationRow } from './MoveSheet';

/** The renewable class behind a pool; the quarantine pool (Flagged) does not renew. */
const POOL_RENEWAL: Partial<Record<Pool, RenewalClass>> = { clean: 'running_hot', dirty: 'dirty_hot' };

/**
 * The renewal word beside a heading — "renews in 12 days", "due now", or
 * "no key yet" when the class's account key is not registered (then nothing
 * renews, and Devices & keys is where to go). Null with no reading.
 */
function RenewalWord({ r }: { r: RenewalRow | null | undefined }) {
  if (!r) return null;
  return (
    <span className="ml-1.5 text-meta font-normal text-tg-hint" title={r.nextDueAt ? `next renewal ${r.nextDueAt}` : undefined}>
      · {r.keyRegistered ? dueWord(r.dueInDays) : 'no key yet'}
    </span>
  );
}

/**
 * Retired addresses of the class that still hold money (slice E): each is
 * watched and read, and its balance moves to the current address only on
 * the operator's Ledger — "move queued" when that card is waiting for him.
 */
function RetiredRows({ r }: { r: RenewalRow | null | undefined }) {
  const rows = (r?.retired ?? []).filter((x) => aboveZero(x.balance));
  if (!r || rows.length === 0) return null;
  return (
    <>
      {rows.map((x) => (
        <div key={x.walletId} className="row">
          <span className="min-w-0 text-body text-tg-hint">
            <span>{retiredLine(x, r.keyRegistered)}</span>
            <Address value={x.address} className="ml-1.5" />
          </span>
        </div>
      ))}
    </>
  );
}

/** The three floats, by the pool screening sends a deposit to; the colour is the score band. */
const POOL_META: Record<Pool, { label: string; blurb: string; icon: typeof Lock; tone: string }> = {
  clean: {
    label: CLASS_WORD['running_hot']!, icon: CheckCircle2, tone: 'text-tg-accent lg:text-pos',
    blurb: 'Scored under 50. The only wallet that pays a customer.',
  },
  dirty: {
    label: CLASS_WORD['dirty_hot']!, icon: Store, tone: 'text-tg-hint lg:text-warn',
    blurb: 'Scored 50 to 69. Isolated — pays merchants only, and is emptied first.',
  },
  quarantine: {
    label: CLASS_WORD['frozen']!, icon: Flag, tone: 'text-tg-destructive lg:text-neg',
    blurb: 'Scored 70+, or a sanctions or darknet hit. Moves only on your decision.',
  },
};

// The pool total — the sum of what was read, and how many that is — lives in
// lib/custody-screen.ts (poolTotal), with its tests.

/** What to render where a number would go when there is no reading. */
function Amount({ w }: { w: WalletBalance }) {
  if (w.balance !== null) return <span className="font-mono text-body tabular">{money(w.balance, w.asset)}</span>;
  return (
    <span className="font-mono text-body text-tg-hint" title={w.asOf ? `last read ${w.asOf}` : 'never read'}>
      —
    </span>
  );
}

function WalletRow({ w, name }: { w: WalletBalance; name: string }) {
  return (
    <div className="row">
      <span className="min-w-0 text-body">
        <span>{name}</span>
        {w.signing === 'multisig_2of3' && <Pill className="ml-1.5">2-of-3</Pill>}
        <Address value={w.address} className="ml-1.5" />
      </span>
      <Amount w={w} />
    </div>
  );
}

function PoolCard({ pool, rows, onMoveToTreasury, renewal }: {
  pool: Pool;
  rows: WalletBalance[];
  /** On Hot only: queue a move to Treasury (step 7 of the flow). Null where there is no treasury wallet. */
  onMoveToTreasury?: (() => void) | null;
  /** The class's renewal reading (slice E): the due word in the header, the retired addresses with money under the rows. */
  renewal?: RenewalRow | null;
}) {
  const meta = POOL_META[pool];
  const Icon = meta.icon;
  const asset = rows[0]?.asset ?? 'USDTTRC';
  // Nothing read is "Not known", never 0; some unread is a subtotal, labelled.
  const total = poolTotal(rows);
  const figure = (
    <span className="font-mono text-figure font-semibold tabular">
      {total.total === null ? <span className="text-tg-hint">Not known</span> : money(total.total, asset)}
    </span>
  );

  return (
    <Card
      // On a phone the name, the renewal word and the figure stack; from sm the
      // figure sits in the card's action slot beside the name (Screens' Astra review, 22 Oct 2026).
      title={
        <span className="flex min-w-0 flex-col gap-0.5 sm:flex-row sm:items-center sm:gap-2">
          <span className="flex items-center gap-2"><Icon className={`h-4 w-4 ${meta.tone}`} />{meta.label}</span>
          <RenewalWord r={renewal} />
          <span className="sm:hidden">{figure}</span>
        </span>
      }
      action={<span className="hidden sm:inline">{figure}</span>}
    >
      {total.read < total.all && (
        <StateLine tone="warn" className="mb-2" more="A wallet the chain did not answer for is outside the figure above; the figure is what was read.">
          {total.read === 0 ? 'No wallet read.' : `${total.read} of ${total.all} read.`}
        </StateLine>
      )}
      {onMoveToTreasury !== undefined && (
        <button
          className="btn-secondary mb-3 flex min-h-[44px] w-full items-center justify-center gap-2 text-body"
          disabled={onMoveToTreasury === null}
          title={onMoveToTreasury === null ? 'No treasury wallet is registered on this chain' : undefined}
          onClick={() => onMoveToTreasury?.()}
        >
          <ArrowRightToLine className="h-3.5 w-3.5" /> Move to Treasury
        </button>
      )}
      {pool !== 'clean' && (
        <StateLine className="mb-2" more="Merchant Hot and Flagged pay only a party the Books mark merchant, each under caps of its own (Flow → Caps). Pay on the merchant's entry in Payout addresses and choose the source there.">
          Pays merchants only — Pay on Payout addresses.
        </StateLine>
      )}
      {rows.map((w) => <WalletRow key={w.id} w={w} name={walletName(w)} />)}
      <RetiredRows r={renewal} />
      <MoreDetails label="What this wallet is">
        <p className="text-meta text-tg-hint">{meta.blurb}</p>
      </MoreDetails>
    </Card>
  );
}

export function Balances() {
  const q = useQuery({ queryKey: ['custody', 'balances'], queryFn: api.balances, refetchInterval: 30_000 });
  // The renewal readings ride beside the balances; a Worker without the route
  // leaves the headings as they were — the balances never wait on it.
  const renewals = useQuery({ queryKey: ['custody', 'renewals'], queryFn: api.renewals, refetchInterval: 60_000, retry: false });
  const [moving, setMoving] = useState<MoveDestinationRow | null>(null);

  const state = listState(q);
  const wallets = q.data?.wallets ?? [];
  const renewalOf = (cls: RenewalClass): RenewalRow | null =>
    renewals.data?.renewals.find((r) => r.class === cls) ?? null;
  const byPool = (p: Pool) => wallets.filter((w) => w.pool === p);
  const fee = wallets.filter((w) => w.class === 'fee');
  const treasury = wallets.filter((w) => w.class === 'treasury');
  const deposits = wallets.filter((w) => w.class === 'deposit');
  // Step 7 of the flow: Hot → Treasury. The desk's treasury wallet on this
  // chain — the first, as the orchestrator routes — or none, and the button says so.
  const vault = treasury[0] ?? null;
  const moveToTreasury = vault
    ? () => setMoving({ id: vault.id, name: walletName(vault), address: vault.address })
    : null;
  // The refill (queue item 3, 23 Oct 2026): the vault fills Hot, signed by the Treasury key — two of its three.
  const [refilling, setRefilling] = useState<MoveDestinationRow | null>(null);
  const hotWallet = wallets.find((w) => w.class === 'running_hot') ?? null;
  const refillHot = hotWallet
    ? () => setRefilling({ id: hotWallet.id, name: walletName(hotWallet), address: hotWallet.address })
    : null;

  const failed = <ReadFailed what="the balances" error={q.error} onRetry={() => void q.refetch()} pending={q.isFetching} />;

  return (
    <div className="space-y-3 lg:space-y-4">
      {/* Mounted whatever the list does: a failed refresh must not take a queued move's sheet with it. */}
      <MoveSheet
        open={moving !== null}
        onClose={() => setMoving(null)}
        title="Move to Treasury"
        verb="Move"
        destination={moving}
        about="From Hot to Treasury. The desk builds the transaction and offers it in Approvals as a treasury move; nothing moves until you sign it there."
      />
      <MoveSheet
        open={refilling !== null}
        onClose={() => setRefilling(null)}
        title="Refill Hot"
        verb="Refill"
        destination={refilling}
        sources={['treasury']}
        about="From Treasury to Hot. The desk builds the transaction and offers it in Approvals as a refill; it needs two of the Treasury's three keys, and nothing moves until they sign it there."
      />
      {state.show === 'failed' ? failed : state.show === 'pending' ? <Pending what="Reading…" /> : (
        <>
          {state.stale && failed}
          {wallets.length === 0 ? (
            <Card>
              <EmptyState action={<Link to="/custody/keys" className="btn-secondary inline-flex min-h-[44px] items-center px-4 text-body lg:min-h-[40px]">Register the Ledger</Link>}>
                No wallet on this chain yet.
              </EmptyState>
            </Card>
          ) : (
            <>
              {q.data && q.data.freshness !== 'fresh' && (
                <StateLine tone="warn" more="Balances are read from the chain, not from the ledger. An old reading is indicative; the desk reads again every few minutes.">
                  Figures not current{q.data.asOf ? ` — oldest reading ${relativeTime(q.data.asOf)}` : ''}.
                </StateLine>
              )}
              {(['clean', 'dirty', 'quarantine'] as Pool[]).map((p) => {
                const rows = byPool(p);
                const cls = POOL_RENEWAL[p];
                return rows.length
                  ? <PoolCard key={p} pool={p} rows={rows} onMoveToTreasury={p === 'clean' ? moveToTreasury : undefined} renewal={cls ? renewalOf(cls) : null} />
                  : null;
              })}

              {fee.length > 0 && (
                <Card title={<span className="flex items-center gap-2"><Zap className="h-4 w-4 text-tg-hint" />{CLASS_WORD['fee']}</span>}>
                  {fee.map((w) => <WalletRow key={w.id} w={w} name={walletName(w)} />)}
                  <MoreDetails label="What this wallet is">
                    <p className="text-meta text-tg-hint">Pays activation and energy, in TRX. Never holds USDT.</p>
                  </MoreDetails>
                </Card>
              )}

              {treasury.length > 0 && (
                <Card title={<span className="flex items-center gap-2"><Lock className="h-4 w-4 text-tg-hint" />{CLASS_WORD['treasury']}<RenewalWord r={renewalOf('treasury')} /></span>}>
                  <button
                    className="btn-secondary mb-3 flex min-h-[44px] w-full items-center justify-center gap-2 text-body"
                    disabled={refillHot === null}
                    title={refillHot === null ? `No ${CLASS_WORD['running_hot']} wallet is registered on this chain` : undefined}
                    onClick={() => refillHot?.()}
                  >
                    <ArrowRightToLine className="h-3.5 w-3.5" /> Refill {CLASS_WORD['running_hot']}
                  </button>
                  {treasury.map((w) => <WalletRow key={w.id} w={w} name={walletName(w)} />)}
                  <RetiredRows r={renewalOf('treasury')} />
                  <MoreDetails label="What this wallet is">
                    <p className="text-meta text-tg-hint">The bulk. Two of three keys to move anything out.</p>
                  </MoreDetails>
                </Card>
              )}
              {deposits.length > 0 && (
                <Card title={CLASS_WORD['deposit']!}>
                  {deposits.map((w) => <WalletRow key={w.id} w={w} name={walletName(w)} />)}
                  <MoreDetails label="What these are">
                    <p className="text-meta text-tg-hint">One address per customer, issued by the desk. Money is swept out of them, never paid from them.</p>
                  </MoreDetails>
                </Card>
              )}
            </>
          )}
        </>
      )}
    </div>
  );
}
